import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Check, Linkedin, Instagram } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom TikTok icon (lucide-react doesn't ship one)
const TikTokIcon = ({ size = 18, strokeWidth = 1.4 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const SOCIALS = [
  {
    key: "linkedin",
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/eterna-motion/?viewAsMember=true",
    Icon: Linkedin,
  },
  {
    key: "instagram",
    label: "Instagram",
    href: "https://www.instagram.com/eternamotion.official?igsh=MXd3NXY1YXh5azl5",
    Icon: Instagram,
  },
  {
    key: "tiktok",
    label: "TikTok",
    href: "https://tiktok.com/@eterna.motion.official",
    Icon: TikTokIcon,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function ComingSoonPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "it";

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Newsletter (inline) state
  const [nlEmail, setNlEmail] = useState("");
  const [nlNotifyLaunch, setNlNotifyLaunch] = useState(true);
  const [nlSubmitting, setNlSubmitting] = useState(false);
  const [nlSubmitted, setNlSubmitted] = useState(false);
  const [nlError, setNlError] = useState(null);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNlSubmitting(true);
    setNlError(null);
    try {
      await axios.post(`${API}/newsletter`, {
        email: nlEmail,
        locale: lang,
        notify_launch: nlNotifyLaunch,
      });
      setNlSubmitted(true);
      setNlEmail("");
    } catch {
      setNlError(t("coming_soon.newsletter_error"));
    } finally {
      setNlSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/private-proposal`, { ...form, locale: lang });
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      setError(t("coming_soon.form_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const services = [
    t("coming_soon.service_1"),
    t("coming_soon.service_2"),
    t("coming_soon.service_3"),
    t("coming_soon.service_4"),
  ];

  return (
    <div
      data-testid="coming-soon-page"
      className="min-h-screen relative overflow-hidden"
      style={{ backgroundColor: "#FAF9F6", color: "#1A1B1A" }}
    >
      <Helmet>
        <html lang={lang} />
        <title>ETERNA Motion | Curated Classic Automobiles in Venice</title>
        <meta name="description" content="ETERNA Motion is a Venice-based automotive atelier for curated classic automobiles, fashion productions, hospitality, private events and visual storytelling." />
        <meta name="robots" content="index,follow" />
        <meta name="keywords" content="ETERNA Motion, Morgan 4/4, Morgan 4/4 1958, Morgan 4/4 Series II, classic automobiles, curated classic cars, automotive atelier, Venice, Venezia, luxury events, private events, hospitality experiences, fashion productions, visual storytelling, bespoke sourcing, scenic presence, classic car for events, classic car for photoshoot, auto d'epoca Venezia, editorial car shoot, wedding classic car, luxury car atelier, vintage car photography" />
        <meta name="author" content="ETERNA Motion" />
        <link rel="canonical" href="https://eternamotion.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://eternamotion.com/" />
        <meta property="og:title" content="ETERNA Motion | Curated Classic Automobiles in Venice" />
        <meta property="og:description" content="ETERNA Motion is a Venice-based automotive atelier for curated classic automobiles, fashion productions, hospitality, private events and visual storytelling." />
        <meta property="og:image" content="https://eternamotion.com/eterna-logo.png" />
        <meta property="og:locale" content={lang === "en" ? "en_US" : "it_IT"} />
        <meta property="og:site_name" content="ETERNA Motion" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ETERNA Motion | Curated Classic Automobiles in Venice" />
        <meta name="twitter:description" content="Venice-based automotive atelier for curated classic automobiles, fashion productions, hospitality, private events and visual storytelling." />
        <meta name="twitter:image" content="https://eternamotion.com/eterna-logo.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://eternamotion.com/#business",
          "name": "ETERNA Motion",
          "image": "https://eternamotion.com/eterna-logo.png",
          "logo": "https://eternamotion.com/eterna-logo.png",
          "url": "https://eternamotion.com",
          "description": "ETERNA Motion is a Venice-based automotive atelier for curated classic automobiles, fashion productions, hospitality, private events and visual storytelling.",
          "email": "contact@eternamotion.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Venezia",
            "addressRegion": "Veneto",
            "addressCountry": "IT"
          },
          "areaServed": ["IT", "EU"],
          "sameAs": [
            "https://www.linkedin.com/company/eterna-motion/",
            "https://www.instagram.com/eternamotion.official",
            "https://tiktok.com/@eterna.motion.official"
          ],
          "knowsAbout": [
            "ETERNA Motion",
            "Classic automobiles",
            "Curated classic cars",
            "Automotive atelier",
            "Venice",
            "Luxury events",
            "Private events",
            "Hospitality experiences",
            "Fashion productions",
            "Visual storytelling",
            "Bespoke sourcing",
            "Scenic presence",
            "Classic car for events",
            "Classic car for photoshoot",
            "Vintage cars",
            "Editorial car shoots"
          ],
          "makesOffer": [
            {"@type": "Offer", "name": "Creative Production", "description": "Cars for fashion, editorial and film"},
            {"@type": "Offer", "name": "Luxury Events", "description": "Classic cars for private events and hospitality"},
            {"@type": "Offer", "name": "ETERNA Bespoke", "description": "Curated sourcing of classic and special automobiles"}
          ]
        })}</script>
      </Helmet>

      {/* Background texture — soft warm wash */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, #C4A462 0, transparent 50%), radial-gradient(circle at 80% 70%, #C4A462 0, transparent 40%)',
        }}
      />
      <div className="absolute inset-0 pointer-events-none grain-overlay opacity-20" />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="px-8 lg:px-16 pt-8 lg:pt-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="flex items-center justify-between"
          >
            <p
              className="text-[10px] uppercase tracking-[0.35em] text-[#757575]"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("coming_soon.location")}
            </p>
            {/* IT / EN switcher */}
            <div className="flex items-center gap-1" data-testid="coming-soon-lang-switcher">
              {[
                { code: "it", label: "IT" },
                { code: "en", label: "EN" },
              ].map((l, idx) => (
                <span key={l.code} className="flex items-center">
                  <button
                    data-testid={`coming-soon-lang-${l.code}`}
                    onClick={() => i18n.changeLanguage(l.code)}
                    className={`text-[11px] uppercase tracking-[0.2em] px-1.5 transition-colors duration-300 ${
                      lang === l.code ? "text-[#C4A462]" : "text-[#757575] hover:text-[#1A1B1A]"
                    }`}
                    style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                  >
                    {l.label}
                  </button>
                  {idx === 0 && <span className="text-[#D8D4CC] text-[10px]">|</span>}
                </span>
              ))}
            </div>
          </motion.div>
        </header>

        <main className="flex-1 px-8 lg:px-16 py-10 lg:py-14">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            {/* LEFT: text */}
            <div className="lg:col-span-7">
              {/* Logo */}
              <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                className="mb-12 lg:mb-16 flex justify-start"
              >
                <img
                  src="/eterna-logo.png"
                  alt="ETERNA Motion"
                  data-testid="coming-soon-logo"
                  className="w-40 sm:w-48 lg:w-56 h-auto"
                  style={{ filter: "drop-shadow(0 4px 20px rgba(196,164,98,0.18))" }}
                />
              </motion.div>

              <motion.p
                initial="hidden" animate="visible" variants={fadeUp} custom={1}
                className="text-xs uppercase tracking-[0.35em] text-[#C4A462] mb-8"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
              >
                {t("coming_soon.eyebrow")}
              </motion.p>

              <motion.h1
                initial="hidden" animate="visible" variants={fadeUp} custom={2}
                data-testid="coming-soon-title"
                className="text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[1.02] text-[#1A1B1A] mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
              >
                {t("coming_soon.title_line_1")}<br />
                <span style={{ fontStyle: "italic", fontWeight: 600, color: "#C4A462" }}>
                  {t("coming_soon.title_line_2_a")}<br />{t("coming_soon.title_line_2_b")}
                </span>
              </motion.h1>

              <motion.div
                initial="hidden" animate="visible" variants={fadeUp} custom={3}
                className="space-y-5 max-w-xl"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
              >
                <p className="text-sm sm:text-base text-[#4A4A4A] leading-relaxed">
                  {t("coming_soon.intro_p1_a")} <span style={{ color: "#1A1B1A", fontWeight: 500 }}>{t("coming_soon.intro_p1_em")}</span> {t("coming_soon.intro_p1_b")}
                </p>
                <p className="text-sm sm:text-base text-[#4A4A4A] leading-relaxed">
                  {t("coming_soon.intro_p2_a")} <span style={{ color: "#C4A462", fontStyle: "italic" }}>{t("coming_soon.intro_p2_em")}</span>{t("coming_soon.intro_p2_b")}
                </p>
              </motion.div>

              <motion.div
                initial="hidden" animate="visible" variants={fadeUp} custom={4}
                className="mt-10 pt-8 border-t border-[#1A1B1A]/10"
              >
                <p
                  className="text-[10px] uppercase tracking-[0.3em] text-[#757575] mb-5"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                >
                  {t("coming_soon.services_label")}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {services.map((item, i) => (
                    <div
                      key={i}
                      data-testid={`atelier-service-${i}`}
                      className="flex items-center gap-3 text-sm text-[#1A1B1A]"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontStyle: "italic" }}
                    >
                      <span className="w-4 h-px bg-[#C4A462]" />
                      {item}
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Newsletter block */}
              <motion.div
                initial="hidden" animate="visible" variants={fadeUp} custom={5}
                className="mt-12 pt-8 border-t border-[#1A1B1A]/10"
                data-testid="coming-soon-newsletter-block"
              >
                <p
                  className="text-[10px] uppercase tracking-[0.3em] text-[#C4A462] mb-3"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                >
                  {t("coming_soon.newsletter_eyebrow")}
                </p>
                <h3
                  className="text-2xl sm:text-3xl tracking-tight text-[#1A1B1A] mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                >
                  {t("coming_soon.newsletter_title")}
                </h3>
                <p
                  className="text-xs sm:text-sm text-[#4A4A4A] leading-relaxed mb-6 max-w-md"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                >
                  {t("coming_soon.newsletter_subtitle")}
                </p>
                <div className="max-w-md">
                  <AnimatePresence mode="wait">
                    {nlSubmitted ? (
                      <motion.div
                        key="nl-success"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        data-testid="coming-soon-newsletter-success"
                        className="flex items-center gap-3 py-4 text-sm text-[#C4A462] border-b border-[#C4A462]/50"
                        style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
                      >
                        <Check strokeWidth={1.5} size={18} />
                        {t("coming_soon.newsletter_success")}
                      </motion.div>
                    ) : (
                      <motion.form
                        key="nl-form"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onSubmit={handleNewsletterSubmit}
                        data-testid="coming-soon-newsletter-form"
                        className="w-full"
                      >
                        <div className="flex items-stretch border-b border-[#1A1B1A]/40 bg-transparent">
                          <input
                            type="email"
                            required
                            value={nlEmail}
                            onChange={(e) => setNlEmail(e.target.value)}
                            data-testid="coming-soon-newsletter-email"
                            placeholder={t("coming_soon.newsletter_email_placeholder")}
                            className="flex-1 bg-transparent px-1 py-3 text-sm text-[#1A1B1A] placeholder:text-[#9A9A9A] focus:outline-none"
                            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                          />
                          <button
                            type="submit"
                            disabled={nlSubmitting}
                            data-testid="coming-soon-newsletter-submit"
                            className="px-2 text-[#1A1B1A] hover:text-[#C4A462] transition-colors duration-300 flex items-center gap-2 text-xs uppercase tracking-[0.15em] disabled:opacity-50"
                            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                          >
                            {nlSubmitting ? "..." : (
                              <>
                                <span className="hidden sm:inline">{t("coming_soon.newsletter_submit")}</span>
                                <Send strokeWidth={1.5} size={16} />
                              </>
                            )}
                          </button>
                        </div>
                        {/* Notify-at-launch checkbox */}
                        <label
                          htmlFor="notify-launch"
                          data-testid="coming-soon-newsletter-notify-label"
                          className="mt-4 flex items-start gap-3 cursor-pointer group"
                        >
                          <span
                            className={`mt-[2px] inline-flex w-4 h-4 shrink-0 items-center justify-center border transition-colors ${
                              nlNotifyLaunch
                                ? "bg-[#C4A462] border-[#C4A462]"
                                : "border-[#1A1B1A]/40 bg-transparent group-hover:border-[#C4A462]"
                            }`}
                          >
                            {nlNotifyLaunch && (
                              <Check strokeWidth={3} size={11} className="text-[#FAF9F6]" />
                            )}
                          </span>
                          <input
                            id="notify-launch"
                            type="checkbox"
                            checked={nlNotifyLaunch}
                            onChange={(e) => setNlNotifyLaunch(e.target.checked)}
                            data-testid="coming-soon-newsletter-notify"
                            className="sr-only"
                          />
                          <span
                            className="text-xs text-[#4A4A4A] leading-snug"
                            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                          >
                            {t("coming_soon.newsletter_notify_launch")}
                          </span>
                        </label>
                        {nlError && (
                          <p data-testid="coming-soon-newsletter-error" className="text-xs text-red-600 mt-3">
                            {nlError}
                          </p>
                        )}
                      </motion.form>
                    )}
                  </AnimatePresence>
                </div>
                <p
                  className="text-[10px] uppercase tracking-[0.25em] text-[#9A9A9A] mt-3"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
                >
                  {t("coming_soon.newsletter_legal")}
                </p>
              </motion.div>

              {/* Social */}
              <motion.div
                initial="hidden" animate="visible" variants={fadeUp} custom={6}
                className="mt-10 pt-6"
                data-testid="coming-soon-socials"
              >
                <p
                  className="text-[10px] uppercase tracking-[0.3em] text-[#757575] mb-4"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                >
                  {t("coming_soon.follow_label")}
                </p>
                <div className="flex items-center gap-4">
                  {SOCIALS.map(({ key, label, href, Icon }) => (
                    <a
                      key={key}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      data-testid={`coming-soon-social-${key}`}
                      className="w-10 h-10 flex items-center justify-center border border-[#1A1B1A]/20 text-[#1A1B1A] hover:bg-[#1A1B1A] hover:text-[#FAF9F6] hover:border-[#1A1B1A] transition-all duration-300"
                    >
                      <Icon strokeWidth={1.4} size={18} />
                    </a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* RIGHT: form */}
            <motion.div
              initial="hidden" animate="visible" variants={fadeUp} custom={5}
              className="lg:col-span-5 lg:sticky lg:top-12"
            >
              <div
                className="relative p-8 sm:p-10 border border-[#1A1B1A]/12"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.7)", backdropFilter: "blur(12px)" }}
              >
                <p
                  className="text-[10px] uppercase tracking-[0.3em] text-[#C4A462] mb-3"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                >
                  {t("coming_soon.form_eyebrow")}
                </p>
                <h2
                  className="text-2xl sm:text-3xl tracking-tight text-[#1A1B1A] mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                >
                  {t("coming_soon.form_title_a")}<span style={{ fontStyle: "italic", color: "#C4A462" }}>{t("coming_soon.form_title_em")}</span>{t("coming_soon.form_title_b")}
                </h2>
                <p
                  className="text-xs text-[#4A4A4A] leading-relaxed mb-8"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                >
                  {t("coming_soon.form_subtitle")}
                </p>

                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="proposal-success"
                    className="py-10 text-center"
                  >
                    <Check strokeWidth={1.25} size={36} className="text-[#C4A462] mx-auto mb-5" />
                    <p
                      className="text-lg text-[#1A1B1A] mb-2"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                    >
                      {t("coming_soon.form_success_title")}
                    </p>
                    <p
                      className="text-xs text-[#4A4A4A] leading-relaxed"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                    >
                      {t("coming_soon.form_success_subtitle")}
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      data-testid="proposal-new"
                      className="mt-6 text-[10px] uppercase tracking-[0.25em] text-[#C4A462] hover:text-[#1A1B1A] transition-colors"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                    >
                      {t("coming_soon.form_new_request")}
                    </button>
                  </motion.div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-testid="proposal-form"
                    className="space-y-5"
                  >
                    {[
                      { key: "name", label: t("coming_soon.form_name"), type: "text", required: true },
                      { key: "email", label: t("coming_soon.form_email"), type: "email", required: true },
                      { key: "phone", label: t("coming_soon.form_phone"), type: "tel", required: false },
                    ].map(({ key, label, type, required }) => (
                      <div key={key}>
                        <label
                          className="block text-[10px] uppercase tracking-[0.2em] text-[#757575] mb-2"
                          style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                        >
                          {label} {required && <span className="text-[#C4A462]">*</span>}
                        </label>
                        <input
                          type={type}
                          required={required}
                          value={form[key]}
                          onChange={(e) => handleChange(key, e.target.value)}
                          data-testid={`proposal-${key}`}
                          className="w-full bg-transparent border-b border-[#1A1B1A]/25 focus:border-[#C4A462] py-2 text-sm text-[#1A1B1A] placeholder:text-[#9A9A9A] focus:outline-none transition-colors"
                          style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                        />
                      </div>
                    ))}

                    <div>
                      <label
                        className="block text-[10px] uppercase tracking-[0.2em] text-[#757575] mb-2"
                        style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                      >
                        {t("coming_soon.form_project")} <span className="text-[#C4A462]">*</span>
                      </label>
                      <textarea
                        required
                        rows={4}
                        value={form.message}
                        onChange={(e) => handleChange("message", e.target.value)}
                        data-testid="proposal-message"
                        className="w-full bg-transparent border-b border-[#1A1B1A]/25 focus:border-[#C4A462] py-2 text-sm text-[#1A1B1A] placeholder:text-[#9A9A9A] focus:outline-none resize-none transition-colors"
                        style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                        placeholder={t("coming_soon.form_project_placeholder")}
                      />
                    </div>

                    {error && (
                      <p data-testid="proposal-error" className="text-xs text-red-600">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="proposal-submit"
                      className="w-full mt-3 bg-[#1A1B1A] text-[#FAF9F6] px-6 py-4 text-[10px] uppercase tracking-[0.25em] hover:bg-[#C4A462] transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                    >
                      {submitting ? t("coming_soon.form_submitting") : (
                        <>
                          {t("coming_soon.form_submit")} <Send strokeWidth={1.5} size={14} />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </main>

        <footer className="px-8 lg:px-16 pb-10 pt-6 border-t border-[#1A1B1A]/10">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
            <p
              className="text-[10px] uppercase tracking-[0.3em] text-[#757575]"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              &copy; {new Date().getFullYear()} {t("coming_soon.footer_copyright")}
            </p>
            <a
              href="mailto:contact@eternamotion.com"
              data-testid="footer-mailto"
              className="text-[10px] uppercase tracking-[0.3em] text-[#C4A462] hover:text-[#1A1B1A] transition-colors"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              contact@eternamotion.com
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}
